import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import CopyScheduleModal from './CopyScheduleModal';
import repositoryContainer from '../../../services/RepositoryContainer';
import { useToast } from '../../../components/context/ToastContext';
import { useAuth } from '../../../components/context/AuthContext';

const TimeSlotManager = ({ selectedDate, timeSlots, onAddSlot, onDeleteSlot, onUpdateSlot, isSaving, isDeleting, onCopySuccess }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [newSlot, setNewSlot] = useState({
    startTime: '',
    endTime: '',
    capacity: 1,
    service: 'all'
  });

  const { addToast } = useToast(); 
  const getServiceOptions = async () => {
    try {
      const services = await repositoryContainer.service.list({ type: 'service'}) || [];
      return [
        { value: 'all', label: 'All Services' },
        ...services.map(service => ({ value: service?.id, label: service?.name }))
      ];
    }catch (error) {
      console.error('Failed to load services:', error);
      return [
        { value: 'all', label: 'All Services' }
      ];
    }
  };

  const { isProOrBusiness } = useAuth();
  const [serviceOptions, setServiceOptions] = useState([]);
  React.useEffect(() => {
    const loadServices = async () => {
      const options = await getServiceOptions();
      setServiceOptions(options);
    };
    loadServices(); 
  }, []);



  const formatDate = (date) => {
    if (!date) return '';
    return date?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleAddSlot = async () => {
    if (!newSlot?.startTime || !newSlot?.endTime) return;

    try {
      await onAddSlot(newSlot);
      setNewSlot({ startTime: '', endTime: '', capacity: 1, service: 'all' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Failed to create time slot:', error);
      addToast('Failed to add time slot. Please try again.', 'error');
    }
  };
  

  const handleCopySchedule = async (selectedDateSlotIds) => {
    setIsCopying(true);
    try {
      if (!timeSlots || timeSlots.length === 0) {
        throw new Error('No time slots to copy. Please ensure the current date has time slots.');
      }

      if (!selectedDateSlotIds || selectedDateSlotIds.length === 0) {
        throw new Error('No target dates selected.');
      }

      // Transform timeSlots to match API format
      const timeSlotsPayload = timeSlots.map(slot => ({
        startTime: slot.startTime,
        endTime: slot.endTime,
        capacity: slot.capacity,
        serviceItemIds: slot.serviceItemIds || []
      }));

      await repositoryContainer.timeSlot.copy(selectedDateSlotIds, timeSlotsPayload);
      setShowCopyModal(false);
      addToast('Time slots copied successfully and applied!', 'success');
      if (typeof onCopySuccess === 'function') {
        onCopySuccess();
      }
    } catch (error) {
      console.error('❌ Failed to copy schedule:', error);
      addToast(error.data?.message || error.message || 'Failed to copy schedule. Please try again.', 'error');
    } finally {
      setIsCopying(false);
    }
  };

  const getSlotStatus = (slot) => {
    if (slot?.blocked) return { label: 'Blocked', color: 'bg-error/20 text-error' };
    if (slot?.booked >= slot?.capacity) return { label: 'Full', color: 'bg-warning/20 text-warning' };
    if (slot?.booked > 0) return { label: `${slot?.booked}/${slot?.capacity} Booked`, color: 'bg-primary/20 text-primary' };
    return { label: 'Available', color: 'bg-success/20 text-success' };
  };

  if (!selectedDate?.date) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-sm p-6 md:p-8 lg:p-12">
        <div className="flex flex-col items-center justify-center text-center py-8 md:py-12">
          <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mb-4 md:mb-6">
            <Icon name="Calendar" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="font-heading font-semibold text-lg md:text-xl lg:text-2xl text-foreground mb-2">
            Select a Date
          </h3>
          <p className="text-sm md:text-base text-muted-foreground font-caption max-w-md">
            Choose a date from the calendar to view and manage time slots for that day
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 md:mb-6">
        <div>
          <h3 className="font-heading font-semibold text-lg md:text-xl lg:text-2xl text-foreground mb-1">
            Time Slots
          </h3>
          <p className="text-sm md:text-base text-muted-foreground font-caption">
            {formatDate(selectedDate?.date)}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex-1 sm:flex-initial"
          >
            Add Time Slot
          </Button>
          {timeSlots?.length > 0 && (
            <Button
              variant="outline"
              iconName="Copy"
              iconPosition="left"
              onClick={() => setShowCopyModal(true)}
              disabled={!isProOrBusiness || isCopying}
              title={!isProOrBusiness ? 'Pro and Business plans only' : undefined}
              className="flex-1 sm:flex-initial"
            >
              {isCopying ? 'Copying...' : 'Copy Schedule'}
            </Button>
          )}
        </div>
      </div>
      {showAddForm && (
        <div className="bg-muted/50 rounded-lg p-4 md:p-6 mb-4 md:mb-6 border border-border">
          <h4 className="font-heading font-semibold text-base md:text-lg text-foreground mb-4">
            Create New Time Slot
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              type="time"
              label="Start Time"
              value={newSlot?.startTime}
              onChange={(e) => setNewSlot({ ...newSlot, startTime: e?.target?.value })}
              required
            />

            <Input
              type="time"
              label="End Time"
              value={newSlot?.endTime}
              onChange={(e) => setNewSlot({ ...newSlot, endTime: e?.target?.value })}
              required
            />

            <Input
              type="number"
              label="Capacity"
              value={newSlot?.capacity}
              onChange={(e) => setNewSlot({ ...newSlot, capacity: parseInt(e?.target?.value) || 1 })}
              min={1}
              max={10}
              required
            />

            <Select
              label="Service Type"
              options={serviceOptions}
              value={newSlot?.service}
              onChange={(value) => setNewSlot({ ...newSlot, service: value })}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="default"
              iconName="Check"
              iconPosition="left"
              onClick={handleAddSlot}
              disabled={isSaving}
              className="flex-1"
            >
              {isSaving ? 'Creating...' : 'Create Slot'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAddForm(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {timeSlots?.length === 0 ? (
          <div className="text-center py-8 md:py-12">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
              <Icon name="Clock" size={24} className="text-muted-foreground" />
            </div>
            <p className="text-sm md:text-base text-muted-foreground font-caption">
              No time slots available for this date
            </p>
          </div>
        ) : (
          timeSlots?.map((slot) => {
            const status = getSlotStatus(slot);
            return (
              <div
                key={slot?.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-background rounded-lg border border-border hover:shadow-md transition-all duration-250"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-caption font-semibold text-base md:text-lg text-foreground">
                      {slot?.startTime} - {slot?.endTime}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-caption ${status?.color}`}>
                      {status?.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs md:text-sm text-muted-foreground font-caption">
                    <span className="flex items-center gap-1">
                      <Icon name="Users" size={14} />
                      Capacity: {slot?.capacity}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Scissors" size={14} />
                      {slot?.service === 'all' ? 'All Services' : slot?.service}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName={slot?.blocked ? "Unlock" : "Lock"}
                    onClick={() => onUpdateSlot(slot?.id, { blocked: !slot?.blocked })}
                  >
                    {slot?.blocked ? 'Unblock' : 'Block'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => onDeleteSlot(slot?.id)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Copy Schedule Modal */}
      {showCopyModal && (
        <CopyScheduleModal
          isOpen={showCopyModal}
          onClose={() => setShowCopyModal(false)}
          onCopy={handleCopySchedule}
          isCopying={isCopying}
        />
      )}
    </div>
  );
};

export default TimeSlotManager;